import './hover-card.css';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApiQuery } from '../../hooks.js';
import { queryString } from '../../lib/api-client.js';
import { compareRoundCandidates, rankViewerCandidates, viewerCandidateColor, viewerRate, type ViewerCandidate } from './viewer-data.js';
import { positionHoverCard } from './hover-position.js';

export interface ElectionHoverTarget {
  uf: string;
  name: string;
  municipalityCode?: string | undefined;
  available: boolean;
  candidates: ViewerCandidate[];
  counted?: number | null | undefined;
}

interface PreviousRound { available: boolean; candidates: ViewerCandidate[] }

export function ElectionHoverCard({ target, turno, cargo, x, y, id = 'election-map-hover-card' }: {
  target: ElectionHoverTarget; turno: 1 | 2; cargo: 'presidente' | 'governador';
  x: number; y: number; width: number; height: number;
  id?: string;
}) {
  const [ready, setReady] = useState(false);
  const anchor = useRef<HTMLSpanElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const targetKey = `${cargo}:${target.uf}:${target.municipalityCode ?? ''}`;
  useEffect(() => {
    setReady(false);
    const timer = window.setTimeout(() => setReady(true), 250);
    return () => window.clearTimeout(timer);
  }, [targetKey]);
  const query = queryString({ cargo, uf: target.uf, municipio: target.municipalityCode ?? '', turno: 1 });
  const previous = useApiQuery<PreviousRound>(['apuracao', query], `/api/apuracao?${query}`, ready && turno === 2 && target.available, { staleTime: 7_200_000 });
  const candidates = rankViewerCandidates(target.candidates).slice(0, 2);
  const historicalCandidates = rankViewerCandidates(previous.data?.candidates).slice(0, 2);
  const changes = previous.data?.available ? compareRoundCandidates(candidates, previous.data.candidates) : [];
  useLayoutEffect(() => {
    const element = card.current;
    if (!element) return;
    let hiddenByScroll = false;
    const position = () => {
      if (hiddenByScroll) return;
      const bounds = anchor.current?.parentElement?.getBoundingClientRect();
      if (!bounds) return;
      element.style.width = `${Math.min(310, window.innerWidth - 24)}px`;
      const placed = positionHoverCard(bounds.left + x, bounds.top + y, element.offsetWidth, element.scrollHeight + 2, window.innerWidth, window.innerHeight);
      Object.assign(element.style, { left: `${placed.left}px`, top: `${placed.top}px`, width: `${placed.width}px`, maxHeight: `${placed.maxHeight}px`, visibility: 'visible' });
    };
    const hide = () => {
      if (document.activeElement?.getAttribute('aria-describedby') === id) { position(); return; }
      hiddenByScroll = true; element.style.visibility = 'hidden';
    };
    position();
    const observer = new ResizeObserver(position);
    observer.observe(element);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', hide, true);
    return () => { observer.disconnect(); window.removeEventListener('resize', position); window.removeEventListener('scroll', hide, true); };
  }, [x, y, targetKey, id]);
  const content = <div ref={card} id={id} className="election-hover-card" role="tooltip" style={{ visibility: 'hidden' }}>
    <div className="election-hover-heading"><span>{target.uf}</span><div><strong>{target.name}</strong><small>{target.available ? `${viewerRate(target.counted)} das seções totalizadas` : 'Arquivo deste turno indisponível'}</small></div></div>
    {target.available && candidates.length ? <div className="election-hover-candidates">{candidates.map((candidate) => <div key={candidate.external_id ?? candidate.number ?? candidate.name}>
      <img src={candidate.photo_url || '/static/avatar.svg'} alt="" referrerPolicy="no-referrer" onError={(event) => { event.currentTarget.src = '/static/avatar.svg'; }} />
      <span><strong>{candidate.name}</strong><small style={{ color: viewerCandidateColor(candidate) }}><i style={{ background: viewerCandidateColor(candidate) }} />{candidate.party} · {candidate.number ?? '—'}</small></span><b>{viewerRate(candidate.percent)}</b>
    </div>)}</div> : <p className="election-hover-empty">Aguardando votos divulgados pelo TSE.</p>}
    {turno === 2 && target.available && <div className="election-hover-comparison"><span>1º turno · 2026</span>{previous.isLoading || !ready ? <small>Consultando…</small> : previous.error ? <small>Consulta indisponível agora</small> : previous.data?.available ? <div>{historicalCandidates.map((candidate) => <span key={candidate.external_id ?? candidate.number ?? candidate.name}><i style={{ background: viewerCandidateColor(candidate) }} /><strong>{candidate.party} · {candidate.number}</strong> {viewerRate(candidate.percent)}</span>)}</div> : <small>Resultado não disponível</small>}</div>}
    {turno === 2 && changes.length > 0 && <><div className="election-hover-variation"><span>Variação</span>{changes.map(({ candidate, delta }) => <small key={candidate.external_id}>{candidate.party} {delta > 0 ? '+' : ''}{new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(delta)} p.p.</small>)}</div><p className="election-hover-disclaimer">Percentuais publicados nos dois turnos, sem pareamento de seções.</p></>}
    <div className="election-hover-source">Fonte: TSE · {cargo === 'presidente' ? 'Presidente' : 'Governador'} · {turno}º turno</div>
  </div>;
  return <><span ref={anchor} aria-hidden="true" className="election-hover-anchor" />{typeof document !== 'undefined' && createPortal(content, document.body)}</>;
}
